import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ApplicationEvaluationOutcome, WSEventType } from '~/enums';
import { logToSentry } from '~/helpers/loggers.helper';
import { wsCardAutoApprovalMessageSchema } from '~/schemas/DTO/websocket';
import { RootState } from '~/store';
import { accessToken } from '~/store/apis/utils/accessToken';

export const useCardApplicationWebSocket = (
  applicationUuid: string
): {
  status: ApplicationEvaluationOutcome | null;
} => {
  const [status, setStatus] = useState<ApplicationEvaluationOutcome | null>(
    null
  );
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const market = useSelector((state: RootState) => state.intl.market);

  useEffect(() => {
    const cancelTimeout = () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };

    const socket = new WebSocket(import.meta.env.VITE_WEBSOCKET_ONBOARDING_URL);

    socket.onopen = async () => {
      const authPayload = {
        action: 'authorization',
        accessToken: await accessToken.get(),
        callbackId: applicationUuid,
        countryCode: market.toLowerCase(),
        eventType: WSEventType.CardAutoApproval,
      };
      socket.send(JSON.stringify(authPayload));
    };

    socket.onmessage = ({ data }: MessageEvent<string>) => {
      try {
        const parsed = JSON.parse(data);
        if (parsed?.action === 'authorization') {
          if (parsed?.responseCode !== 200) {
            throw new Error(
              `WebSocket auth failed. ResponseCode: ${parsed.responseCode}, Message: ${parsed?.responseMessage}`
            );
          }
          timeoutRef.current = setTimeout(() => {
            setStatus(ApplicationEvaluationOutcome.ManualReview);
            socket.close();
          }, Number(import.meta.env.VITE_WS_NOT_APPROVED_TIMEOUT_MS));
          return;
        }

        const result = wsCardAutoApprovalMessageSchema.safeParse(parsed);

        if (result.success) {
          cancelTimeout();
          const outcome = result.data.message.outcome;
          setStatus(outcome);
          socket.close();
        }
      } catch (error) {
        console.error('Error handling WebSocket message:======', error);
        logToSentry(
          error,
          'useCardApplicationWebSocket onmessage parsing error'
        );
        setStatus(ApplicationEvaluationOutcome.ManualReview);
        socket.close();
      }
    };

    socket.onerror = (error: Event) => {
      logToSentry(error, 'useCardApplicationWebSocket onerror');
      setStatus(ApplicationEvaluationOutcome.ManualReview);
      socket.close();
    };

    return () => {
      cancelTimeout();
      socket.onmessage = null;

      if (socket.readyState === WebSocket.OPEN) {
        socket.close();
      } else {
        socket.onopen = () => socket.close();
      }
    };
  }, [applicationUuid]);

  return { status };
};
